import React, { useMemo } from "react";
import { Autocomplete, ListSubheader, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// LC-481: org names are shown without the internal channel suffix
import { stripChannelSuffix } from "../../../utils/originator-company-name";

// Styles
import styles from "./index.module.scss";
import "./mui-styles.scss";

const SecondarySelectLOLP = ({
	id,
	fullWidth,
	value,
	handleChange,
	disabled,
	error,
	options,
	extraClass,
	listboxClassName,
	showOrgName,
	groupBy,
	separatorBeforeGroup,
	hideGroupLabel,
	getGroupLabel,
	placeholder = "Start typing to search...",
}) => {
	const customClass = `${styles.secondarySelect} ${extraClass} ${fullWidth ? styles.fullWidth : ""
		} ${error ? styles.error : ""}`;

	// Ensure options are unique by key (broker_id or value)
	const uniqueOptions = useMemo(() => {
		const map = new Map();
		options.forEach((opt) => {
			const key = showOrgName ? String(opt.broker_id) : opt.value;
			if (!map.has(key)) {
				map.set(key, opt);
			}
		});
		return Array.from(map.values());
	}, [options, showOrgName]);

	// Filter options based on input value (for controlled filtering)
	const filteredOptions = useMemo(() => {
		return uniqueOptions;
	}, [uniqueOptions]);

	// Find selected option based on value (broker_id or email)
	const selectedOption =
		uniqueOptions.find((item) =>
			showOrgName
				? String(item.broker_id) === String(value)
				: item.value === value
		) || null;

	return (
		<Autocomplete
			id={`autocomplete-${id}`}
			options={filteredOptions}
			disableClearable={false}
			disabled={disabled}
			value={selectedOption}
			getOptionLabel={(option) => option?.value || ""}
			filterSelectedOptions
			isOptionEqualToValue={(option, val) =>
				showOrgName
					? String(option?.broker_id) === String(val)
					: option?.value === val
			}
			filterOptions={(opts, { inputValue }) => {
				const normalized = inputValue.toLowerCase().trim();
				if (!normalized) return opts;
				return opts.filter(
					(opt) =>
						opt.isSectionHeader ||
						opt.value?.toLowerCase().includes(normalized)
				);
			}}
			getOptionDisabled={(option) => option.isSectionHeader === true}
			groupBy={groupBy}
			renderGroup={
				groupBy
					? (params) => (
						<React.Fragment key={params.key}>
							{params.group === separatorBeforeGroup && (
								<li><hr /></li>
							)}
							{params.group !== hideGroupLabel && (
								<ListSubheader component="li" disableSticky>
									{getGroupLabel ? getGroupLabel(params.group) : params.group}
								</ListSubheader>
							)}
							{params.children}
						</React.Fragment>
					)
					: undefined
			}
			onChange={(event, newValue) => {
				if (newValue) {
					const newVal = showOrgName ? newValue.broker_id : newValue.value;
					handleChange({ target: { value: newVal } });
				} else {
					handleChange({ target: { value: "" } });
				}
			}}
			popupIcon={<ExpandMoreIcon />}
			className={customClass}
			ListboxProps={{ className: listboxClassName }}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="outlined"
					placeholder={placeholder}
					error={error}
					InputLabelProps={{ shrink: true }}
					InputProps={{
						...params.InputProps,
						className: styles.secondarySelectInput,
						startAdornment: null,
						endAdornment:
							showOrgName && selectedOption?.organization?.name ? (
								<div className={styles.inputContentRow}>
									<span
										className={styles.orgName}
										style={{ color: disabled ? "#A0A0A0" : undefined }} // Replace with correct disabled color
									>
										<lolpCellHeight>{stripChannelSuffix(selectedOption.organization.name)}</lolpCellHeight>
									</span>
									{params.InputProps?.endAdornment}
								</div>
							) : (
								params.InputProps?.endAdornment
							),
					}}
				/>
			)}
			renderOption={(props, option) =>
				option.isSectionHeader ? (
					<li {...props} key={option.value} role="presentation">
						<ListSubheader
							component="div"
							disableSticky
							className={styles.sectionHeader}
						>
							{option.label}
							<hr />
						</ListSubheader>
					</li>
				) : (
					<li {...props} key={showOrgName ? option.broker_id : option.value}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								width: "100%",
								gap: 2,
							}}
						>
							<lolpCellHeight>{option.value}</lolpCellHeight>
							{showOrgName && option.organization?.name && (
								<lolpCellHeight>{stripChannelSuffix(option.organization.name)}</lolpCellHeight>
							)}
						</div>
					</li>
				)
			}
		/>
	);
};

export default SecondarySelectLOLP;
